import React, { useEffect, useState } from 'react';
import azureDevOpsService from './services/azureDevOpsService';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function KpiBugsMTTRBySquad() {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState('');

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      const bugs = await azureDevOpsService.getBugsWithTA();
      
      // Filtrer uniquement les bugs résolus
      const resolvedBugs = bugs.filter(bug => {
        const state = bug.state;
        return state === '6- Done' || state === '6- Cancelled' || state === '5- Validation';
      });
      
      // Grouper les bugs par squad et calculer MTTR
      const squadMTTR = {};
      const resolutionDateCache = {};
      
      setProgress(`Traitement de ${resolvedBugs.length} bugs résolus...`);
      
      // Traiter les bugs par batch de 10 pour éviter trop d'appels simultanés
      const batchSize = 10;
      for (let i = 0; i < resolvedBugs.length; i += batchSize) {
        const batch = resolvedBugs.slice(i, i + batchSize);
        
        setProgress(`Traitement des bugs ${i + 1} à ${Math.min(i + batchSize, resolvedBugs.length)} sur ${resolvedBugs.length}...`);
        
        await Promise.all(batch.map(async (bug) => {
          const areaPath = bug.areaPath || '';
          let squad;
          
          // Vérifier si le bug fait partie d'un squad
          if (areaPath.startsWith('Fabrique\\Tribus\\Tribu Argonos\\Squads\\')) {
            squad = areaPath.replace('Fabrique\\Tribus\\Tribu Argonos\\Squads\\', '');
            // Regrouper les squads "Etendu" avec leur squad parent
            squad = squad.replace(/\\Etendu$/, '');
          } else {
            // Tous les bugs hors squads vont dans "Autres"
            squad = 'Autres';
          }
          
          const createdDate = new Date(bug.creationDate);
          
          // Vérifier le cache d'abord
          let resolvedDateStr = resolutionDateCache[bug.id];
          
          if (!resolvedDateStr) {
            // Récupérer la vraie date de résolution depuis l'historique
            resolvedDateStr = await azureDevOpsService.getBugResolutionDate(bug.id, bug.state);
            resolutionDateCache[bug.id] = resolvedDateStr;
          }
          
          if (!resolvedDateStr) {
            console.warn(`Impossible de trouver la date de résolution pour le bug ${bug.id}`);
            return;
          }
          
          const resolvedDate = new Date(resolvedDateStr);
          
          // Calculer le temps de résolution en jours
          const timeToResolve = (resolvedDate - createdDate) / (1000 * 60 * 60 * 24);
          
          // Ignorer les valeurs négatives (bugs mal datés)
          if (timeToResolve < 0) {
            console.warn(`Temps de résolution négatif pour le bug ${bug.id}: ${timeToResolve} jours`);
            return;
          }
          
          if (!squadMTTR[squad]) {
            squadMTTR[squad] = {
              totalTime: 0,
              count: 0,
            };
          }
          
          squadMTTR[squad].totalTime += timeToResolve;
          squadMTTR[squad].count++;
        }));
      }
      
      setProgress('Génération du graphique...');
      
      console.log('squadMTTR:', squadMTTR);
      console.log('Nombre de squads:', Object.keys(squadMTTR).length);
      
      // Calculer la moyenne pour chaque squad, avec 'Autres' à la fin
      const sortedSquads = Object.keys(squadMTTR).sort((a, b) => {
        if (a === 'Autres') return 1;
        if (b === 'Autres') return -1;
        return a.localeCompare(b);
      });
      const mttrValues = sortedSquads.map(squad => {
        const data = squadMTTR[squad];
        return data.count > 0 ? (data.totalTime / data.count).toFixed(1) : 0;
      });
      
      const bugCounts = sortedSquads.map(squad => squadMTTR[squad].count);
      
      console.log('Squads:', sortedSquads);
      console.log('MTTR values:', mttrValues);
      console.log('Bug counts:', bugCounts);
      
      setChartData({
        labels: sortedSquads,
        datasets: [
          {
            label: 'MTTR (jours)',
            data: mttrValues,
            backgroundColor: 'rgba(59, 130, 246, 0.7)',
            borderColor: 'rgba(59, 130, 246, 1)',
            borderWidth: 1,
          },
        ],
      });
      
      console.log('ChartData défini, fin du chargement');
      setLoading(false);
    }
    fetchData();
  }, []);

  return (
    <div className="bg-white rounded shadow p-6">
      <h2 className="text-lg font-bold mb-4">MTTR des bugs TA résolus par squad</h2>
      {loading || !chartData ? (
        <div>
          <div>Chargement...</div>
          {progress && <div className="text-sm text-gray-600 mt-2">{progress}</div>}
        </div>
      ) : (
        <Bar
          data={chartData}
          options={{
            responsive: true,
            plugins: {
              legend: { position: 'top' },
              title: {
                display: true,
                text: 'Temps moyen de résolution (MTTR) des bugs TA par squad',
              },
              tooltip: {
                callbacks: {
                  label: function(context) {
                    const value = context.parsed.y;
                    return `MTTR: ${value} jours`;
                  }
                }
              }
            },
            scales: {
              x: { 
                title: { display: true, text: 'Squad' },
                ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 }
              },
              y: { 
                title: { display: true, text: 'MTTR (jours)' }, 
                beginAtZero: true 
              },
            },
          }}
        />
      )}
    </div>
  );
}
